import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatLocalDate } from "@/utils/dateUtils";
import { eventoLabelParaAgencia } from "@/components/timeline/timelineEventUtils";
import type { TimelineEvento } from "@/types/timeline";

// A RPC get_agencia_timeline (sql/150) ainda não está no types.ts gerado -> cast,
// mesmo padrão do useFreelancerTimelineCompleta/useDisponibilidade.
const timelineDb = supabase as unknown as {
  rpc: (functionName: string, args: Record<string, unknown>) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export type PeriodoTimeline = "12m" | "24m" | "tudo";

export interface AgenciaVagaTransicao {
  status_origem: string | null;
  status_destino: string;
  occurred_at: string;
  inferred: boolean;
  metadata: Record<string, unknown>;
}

/** Ciclo de vida de uma vaga da agência: da criação ao status terminal (ou hoje). */
export interface AgenciaVagaTimeline {
  vaga_id: string;
  titulo: string;
  status_atual: string;
  criada_em: string;
  terminal_em: string | null;
  transicoes: AgenciaVagaTransicao[];
}

/** Uma barra do mini-gantt: uma cadeia de renovação (freelancer x agência) já colapsada. */
export interface AgenciaProjetoGantt {
  chain_id: string;
  freelancer_id: string;
  freelancer_nome: string;
  /** Data lógica "YYYY-MM-DD". */
  ini: string | null;
  /** Data lógica "YYYY-MM-DD"; null enquanto aberto. */
  fim: string | null;
  aberto: boolean;
  n_renovacoes: number | null;
}

/** Um dia da série de carga (get_agencia_carga_diaria). `dia` é data lógica "YYYY-MM-DD". */
export interface AgenciaCargaDiariaPonto {
  dia: string;
  freelancers_ativos: number;
  projetos_ativos: number;
}

export interface AgenciaAvaliacaoInterna {
  membro: string | null;
  notas: Record<string, unknown> | null;
  comentario: string | null;
  occurred_at: string;
}

/** Golden record da agência (v_agencia_resumo, sql/150). */
export interface AgenciaResumo {
  agencia_id: string;
  agencia_nome: string;
  vagas_abertas: number;
  vagas_aprovadas: number;
  vagas_reprovadas: number;
  projetos_ativos_agora: number;
  freelancers_alocados_agora: number;
  freelancers_historico_total: number;
  avaliacoes_dadas_count: number;
  avaliacoes_dadas_media: number | null;
  avaliacoes_recebidas_count: number;
  avaliacoes_recebidas_media: number | null;
  ultima_vaga_criada_em: string | null;
  ultima_aprovacao_em: string | null;
}

/** Linha de v_agencia_freelancer_relacao (sql/150): 1 par agência x freelancer. */
export interface AgenciaTopFreelancer {
  agencia_id: string;
  freelancer_id: string;
  freelancer_nome: string;
  vagas_como_candidato: number;
  vezes_aprovado: number;
  vezes_preterido: number;
  projetos_juntos: number;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  media_avaliacao_dada: number | null;
  avaliacoes_dadas_count: number;
  indicacao_radar_ativa: boolean;
}

export interface AgenciaTimelineData {
  eventos: TimelineEvento[];
  vagas: AgenciaVagaTimeline[];
  projetos_gantt: AgenciaProjetoGantt[];
  carga_diaria: AgenciaCargaDiariaPonto[];
  avaliacoes_internas: AgenciaAvaliacaoInterna[];
  golden: {
    resumo: AgenciaResumo | null;
    top_freelancers: AgenciaTopFreelancer[];
  };
}

/** "tudo" = janela larga o suficiente pra cobrir qualquer histórico da plataforma. */
const INICIO_PLATAFORMA = "2015-01-01";

function periodoParaDataInicio(periodo: PeriodoTimeline): string | null {
  if (periodo === "tudo") return INICIO_PLATAFORMA;
  if (periodo === "12m") {
    const inicio = new Date();
    inicio.setMonth(inicio.getMonth() - 12);
    return formatLocalDate(inicio) ?? null;
  }
  // 24m é o default da RPC (p_from null).
  return null;
}

/**
 * Linha do tempo completa de UMA agência (eventos + vagas + mini-gantt de
 * projetos + carga diária + avaliações internas + golden records) via RPC
 * get_agencia_timeline (sql/150). O nome do freelancer de cada evento vem no
 * próprio payload (freelancer_nome, migration 154): buscar nomes num segundo
 * request com id=in.(...) estourava o limite de URL do nginx com 366 ids
 * (414 Request-URI Too Large). Aplica o rótulo por perspectiva (§2.4 do
 * plano: mesma linha-fonte, dois pontos de vista). queryKey com shape
 * próprio: nunca reusar com SELECT/resposta diferente.
 */
export function useAgenciaTimeline(agenciaId?: string | null, periodo: PeriodoTimeline = "24m") {
  return useQuery({
    queryKey: ["agencia-timeline", agenciaId, periodo],
    enabled: !!agenciaId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AgenciaTimelineData | null> => {
      const rpcArgs: Record<string, unknown> = { p_agencia_id: agenciaId };
      const dataInicio = periodoParaDataInicio(periodo);
      if (dataInicio) rpcArgs.p_from = dataInicio;
      const { data, error } = await timelineDb.rpc("get_agencia_timeline", rpcArgs);
      if (error) throw error;
      const bruto = (data as AgenciaTimelineData) ?? null;
      if (!bruto) return null;

      const eventosEnriquecidos = bruto.eventos.map((evento) => ({
        ...evento,
        label: eventoLabelParaAgencia(evento),
      }));

      return { ...bruto, eventos: eventosEnriquecidos };
    },
  });
}
